import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { ArrowLeft, Trophy, Loader2, Save, Plus, Minus } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useLocation, useParams, useSearch } from "wouter";

interface PendingMatch {
  id: string;
  leagueId?: string;
  leagueName?: string;
  team1Player1Id: string;
  team1Player1Name: string;
  team1Player2Id?: string;
  team1Player2Name?: string;
  team2Player1Id: string;
  team2Player1Name: string;
  team2Player2Id?: string;
  team2Player2Name?: string;
  winner: number;
  score: any;
  status: string;
  isGlobalMatch?: boolean;
  reportedById: string;
  reporterTeam: number;
}

export function EditMatchPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const params = useParams<{ matchId: string }>();
  const matchId = params.matchId;
  
  const searchString = useSearch();
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return {
      type: params.get("type") || "global",
      leagueId: params.get("leagueId") || undefined,
    };
  }, [searchString]);

  const [score, setScore] = useState<Array<{ myTeam: number | null; opponents: number | null }>>([
    { myTeam: null, opponents: null }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: pendingMatches = [], isLoading } = useQuery<PendingMatch[]>({
    queryKey: ["/api/matches/pending"],
  });

  const match = useMemo(() => {
    return pendingMatches.find(m => m.id === matchId);
  }, [pendingMatches, matchId]);

  useEffect(() => {
    if (match?.score) {
      const parsedScore = typeof match.score === 'string' 
        ? match.score.split(',').map((s: string) => {
            const [t1, t2] = s.trim().split('-').map(Number);
            return { myTeam: t1, opponents: t2 };
          })
        : match.score.sets?.map((s: { team1: number; team2: number }) => {
            const userTeam = match.team1Player1Id === user?.id || match.team1Player2Id === user?.id ? 1 : 2;
            return userTeam === 1 
              ? { myTeam: s.team1, opponents: s.team2 }
              : { myTeam: s.team2, opponents: s.team1 };
          }) || [];
      
      if (parsedScore.length > 0) {
        setScore(parsedScore);
      }
    }
  }, [match, user?.id]);

  const updateMatchMutation = useMutation({
    mutationFn: async (data: { score: any; winner: number }) => {
      const endpoint = queryParams.type === 'global'
        ? `/api/matches/global/${matchId}`
        : `/api/leagues/${queryParams.leagueId}/matches/${matchId}/pending`;
      
      return apiRequest('PATCH', endpoint, data);
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: "Partido actualizado",
          description: "El marcador ha sido actualizado correctamente",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
        setLocation("/");
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.message || "No se pudo actualizar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Error de conexión",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!match) return;

    const userTeam = match.team1Player1Id === user?.id || match.team1Player2Id === user?.id ? 1 : 2;
    
    const scoreObj = { 
      sets: score.map(s => userTeam === 1 
        ? { team1: s.myTeam || 0, team2: s.opponents || 0 }
        : { team1: s.opponents || 0, team2: s.myTeam || 0 }
      ) 
    };
    
    const myTeamWins = score.filter(s => (s.myTeam || 0) > (s.opponents || 0)).length;
    const opponentsWins = score.filter(s => (s.opponents || 0) > (s.myTeam || 0)).length;
    const winner = userTeam === 1 
      ? (myTeamWins > opponentsWins ? 1 : 2)
      : (myTeamWins > opponentsWins ? 2 : 1);

    updateMatchMutation.mutate({ score: scoreObj, winner });
  };

  const addSet = () => {
    if (score.length < 3) {
      setScore([...score, { myTeam: null, opponents: null }]);
    }
  };

  const removeSet = () => {
    if (score.length > 1) {
      setScore(score.slice(0, -1));
    }
  };

  const updateScore = (index: number, team: 'myTeam' | 'opponents', value: number | null) => {
    const newScore = [...score];
    newScore[index] = { ...newScore[index], [team]: value };
    setScore(newScore);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!match) {
    return (
      <div 
        className="flex flex-col bg-background overflow-hidden"
        style={{ height: '100dvh' }}
      >
        <header className="flex-shrink-0 bg-background/95 backdrop-blur border-b z-50 safe-area-top">
          <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => history.back()}
              data-testid="button-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">Editar Partido</h1>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="container max-w-2xl mx-auto px-4 py-8 text-center">
            <p className="text-muted-foreground">No se encontró el partido o no tienes permiso para editarlo.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setLocation("/")}
            >
              Volver al inicio
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const userTeam = match.team1Player1Id === user?.id || match.team1Player2Id === user?.id ? 1 : 2;
  const myTeamNames = userTeam === 1
    ? [match.team1Player1Name, match.team1Player2Name].filter(Boolean).join(" / ")
    : [match.team2Player1Name, match.team2Player2Name].filter(Boolean).join(" / ");
  const opponentNames = userTeam === 1
    ? [match.team2Player1Name, match.team2Player2Name].filter(Boolean).join(" / ")
    : [match.team1Player1Name, match.team1Player2Name].filter(Boolean).join(" / ");

  const myTeamWins = score.filter(s => (s.myTeam || 0) > (s.opponents || 0)).length;
  const opponentsWins = score.filter(s => (s.opponents || 0) > (s.myTeam || 0)).length;
  const matchWinner = myTeamWins > opponentsWins ? "myTeam" : myTeamWins < opponentsWins ? "opponents" : null;

  const canSubmit = score.every(s => s.myTeam !== null && s.opponents !== null);

  return (
    <div 
      className="flex flex-col bg-background overflow-hidden"
      style={{ height: '100dvh' }}
    >
      <header className="flex-shrink-0 bg-background/95 backdrop-blur border-b z-50 safe-area-top">
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Editar Partido</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto overscroll-contain">
        <div className="container max-w-2xl mx-auto px-4 pt-3 pb-4 safe-area-bottom">
          <Card data-testid="card-edit-match" className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-orange-500/10 to-orange-500/5 pb-4">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-orange-500" />
                Editar Marcador
              </CardTitle>
              {match.leagueName && (
                <Badge variant="outline" className="w-fit">{match.leagueName}</Badge>
              )}
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="text-center">
                    <Badge className="bg-green-600 text-white mb-2">Tu Equipo</Badge>
                    <p className="text-sm font-medium truncate">{myTeamNames}</p>
                  </div>
                  <div className="text-center">
                    <Badge variant="outline" className="border-red-300 text-red-700 dark:text-red-400 mb-2">Rivales</Badge>
                    <p className="text-sm font-medium truncate">{opponentNames}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      Marcador
                    </Label>
                    <div className="flex gap-2">
                      {score.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={removeSet}
                        >
                          <Minus className="h-4 w-4 mr-1" />
                          Set
                        </Button>
                      )}
                      {score.length < 3 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addSet}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Set
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {score.map((set, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm font-medium text-muted-foreground w-12">Set {index + 1}</span>
                        <Select
                          value={set.myTeam?.toString() || ""}
                          onValueChange={(v) => updateScore(index, 'myTeam', v ? parseInt(v) : null)}
                        >
                          <SelectTrigger className="w-16" data-testid={`select-my-score-${index}`}>
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            {[0,1,2,3,4,5,6,7].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-muted-foreground">-</span>
                        <Select
                          value={set.opponents?.toString() || ""}
                          onValueChange={(v) => updateScore(index, 'opponents', v ? parseInt(v) : null)}
                        >
                          <SelectTrigger className="w-16" data-testid={`select-opponent-score-${index}`}>
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            {[0,1,2,3,4,5,6,7].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  {matchWinner && (
                    <div className={`text-center py-2 px-4 rounded-lg font-medium text-sm ${
                      matchWinner === "myTeam" 
                        ? "bg-green-500/20 text-green-700 dark:text-green-400" 
                        : "bg-red-500/20 text-red-700 dark:text-red-400"
                    }`}>
                      {matchWinner === "myTeam" 
                        ? `Ganaste ${myTeamWins} - ${opponentsWins}`
                        : `Perdiste ${opponentsWins} - ${myTeamWins}`
                      }
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white shadow-lg font-semibold text-base" 
                  size="lg"
                  disabled={!canSubmit || updateMatchMutation.isPending}
                  data-testid="button-save-match"
                >
                  {updateMatchMutation.isPending ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-5 w-5 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default EditMatchPage;
